<script>
	import { onDestroy, onMount, tick } from 'svelte';
	import { getApiUrl, getDivisionTitle, injectGlobalStyles, loadJson, normalizeCollection, playerUrl } from './shared.js';
	import WidgetSelect from './WidgetSelect.svelte';

	export let projectId = '';
	export let tag = 'mlbl';

	let host;
	let divisions = [];
	let selectedLeagueId = '';
	let selectedLeagueTag = '';
	let loadingDivisions = false;
	let loadingPoints = false;
	let points = [];
	let assists = [];
	let rebounds = [];
	let loadingAssists = false;
	let loadingRebounds = false;
	let errorMessage = '';
	let syncingFromGlobal = false;

	const LEAGUE_CHANGED_EVENT = 'ilb:league-changed';

	$: leagueOptions = buildLeagueOptions(divisions);

	function getLeagueSelectionStore() {
		const globalScope = window;
		if (!globalScope.__ilbSelectedLeagueByProject) {
			globalScope.__ilbSelectedLeagueByProject = {};
		}
		return globalScope.__ilbSelectedLeagueByProject;
	}

	function getStoredLeagueId() {
		if (!projectId) return '';
		return getLeagueSelectionStore()[projectId] || '';
	}

	function rememberLeagueId(leagueId) {
		if (!projectId || !leagueId) return;
		getLeagueSelectionStore()[projectId] = leagueId;
	}

	function publishLeagueSelection(leagueId) {
		if (!projectId || !leagueId) return;
		window.dispatchEvent(new CustomEvent(LEAGUE_CHANGED_EVENT, {
			detail: { projectId, leagueId }
		}));
	}

	function handleGlobalLeagueChange(event) {
		const payload = event?.detail;
		if (!payload || payload.projectId !== projectId) return;
		if (!payload.leagueId || payload.leagueId === selectedLeagueId) return;
		if (!leagueOptions.some((item) => item.value === payload.leagueId)) return;
		syncingFromGlobal = true;
		selectedLeagueId = payload.leagueId;
	}

	function buildLeagueOptions(items) {
		return (items || []).map((division, index) => ({
			value: division?.id || division?._id || division?.providerId || `division-${index}`,
			label: getDivisionTitle(division)
		}));
	}

	function getLeagueTag(league) {
		return league?.tag || league?.Tag || '';
	}

	function getSelectedLeague() {
		return divisions.find((division) => {
			const id = division?.id || division?._id || division?.providerId || '';
			return id && String(id) === String(selectedLeagueId);
		}) || null;
	}

	async function loadDivisions() {
		if (!projectId) { divisions = []; return; }
		loadingDivisions = true;
		errorMessage = '';
		try {
			const url = `${getApiUrl()}/stats/project/${encodeURIComponent(projectId)}/leagues`;
			let rawDivisions = normalizeCollection(await loadJson(url));

			divisions = rawDivisions;

			const options = buildLeagueOptions(divisions);
			const storedLeagueId = getStoredLeagueId();

			if (storedLeagueId && options.some((item) => item.value === storedLeagueId)) {
				selectedLeagueId = storedLeagueId;
			} else if (!selectedLeagueId || !options.some((item) => item.value === selectedLeagueId)) {
				selectedLeagueId = options[0]?.value || '';
			}

		} catch (error) {
			console.error("Load Divisions Error:", error);
			errorMessage = `Ошибка загрузки дивизионов`;
		} finally { 
			loadingDivisions = false; 
		}
	}

	// Реактивный блок: следит за изменением selectedLeagueId
	$: {
		if (selectedLeagueId && !loadingDivisions) {
			rememberLeagueId(selectedLeagueId);
			if (!syncingFromGlobal) {
				publishLeagueSelection(selectedLeagueId);
			} else {
				syncingFromGlobal = false;
			}
			loadRatings(selectedLeagueId);
		}
	}

	/**
	 * Загружает рейтинги для указанного дивизиона.
	 * @param divisionId
	 */	
	async function loadRatings(divisionId) {     
		selectedLeagueTag = getLeagueTag(getSelectedLeague());
		loadingPoints = true;
		loadingAssists = true;
		loadingRebounds = true;

		const pointsPromise = loadRating(divisionId, 'avgPoints')
			.then(result => {
				points = result;
			})
			.finally(() => {
				loadingPoints = false;
			});

		const assistsPromise = loadRating(divisionId, 'avgAssists')
			.then(result => {
				assists = result;
			})
			.finally(() => {
				loadingAssists = false;
			});

		const reboundsPromise = loadRating(divisionId, 'avgRebounds')
			.then(result => {
				rebounds = result;
			})
			.finally(() => {
				loadingRebounds = false;
			});

		await Promise.all([pointsPromise, assistsPromise, reboundsPromise]);
	}

	/**
	 * Загружает рейтинг для указанного дивизиона и параметра рейтинга.
	 * @param divisionId - ид дивизиона
	 * @param rating - параметр рейтинга
	 */
	async function loadRating(divisionId, rating) {
		return normalizeCollection(await loadJson(`${getApiUrl()}/stats/competition/${encodeURIComponent(divisionId)}/best-players?param=${encodeURIComponent(rating)}`));
	}

	/**
	 * Открывает страницу игрока в рейтинге
	 * @param player
	 */
	function selectPlayer(player) {
		const personId = player?.player?.providerId;
		if (!personId) return;
		window.location.href = playerUrl(personId, { tag: selectedLeagueTag || tag, compId: selectedLeagueId });
	}

	onMount(() => {
		const root = host?.getRootNode();
		if (root instanceof ShadowRoot) injectGlobalStyles(root);
		window.addEventListener(LEAGUE_CHANGED_EVENT, handleGlobalLeagueChange);
		loadDivisions();
	});

	onDestroy(() => {
		window.removeEventListener(LEAGUE_CHANGED_EVENT, handleGlobalLeagueChange);
	});
</script>

<div bind:this={host}>
	{#if errorMessage}
		<div class="widget-status" style="color: red;">{errorMessage}</div>
	{:else if loadingDivisions}
		<div class="widget-status"><ilb-loader size="64px"></ilb-loader></div>
	{:else if !divisions.length}
		<div class="widget-status">Дивизионы не найдены</div>
	{:else}
		<section class="leaders">
			<div class="container">
				<div class="headline">
					<h2 class="leaders__title">Лидеры</h2>

					<WidgetSelect 
						className="--black" 
						name="league" 
						options={leagueOptions} 
						bind:value={selectedLeagueId} 
						disabled={loadingDivisions} 
					/>
				</div>

				<div class="leaders__tabs">
					<button type="button" class="leaders__tab --active" data-tab="points">Очки</button>
					<button type="button" class="leaders__tab" data-tab="rebounds">Подборы</button>
					<button type="button" class="leaders__tab" data-tab="assists">Передачи</button>
				</div>

				<div class="leaders__row">
					<div class="leaders__col --active" data-panel="points">
						<h3 class="leaders__col-title">Очки</h3>
						{#if loadingPoints}
							<div class="widget-status"><ilb-loader size="64px"></ilb-loader></div>
						{:else if points.length > 0}
						<ul class="leaders__list">
							{#each points as player, index}
							<li class="leaders__player" on:click={() => selectPlayer(player)}>
								<div class="leaders__avatar">
									<img src={player.player.photo} alt={player.player.lastName} class="leaders__photo {index === 0 ? '--orange' : ''}">
									<span class="leaders__rank {index === 0 ? '--orange' : ''}">{player.rank}</span>
								</div>
								<div class="leaders__info">
									<div class="leaders__info-top">
										<div class="leaders__name">
											<span>{player.player.lastName}</span>
											<div class="leaders__name-row">
												<span>{player.player.firstName}</span>
												<!--<span class="leaders__jersey">#{player.jersey}</span>-->
											</div>
										</div>
										<span class="leaders__score">{player.displayValue}</span>
									</div>
									<div class="leaders__team">
										<img src={player.team.logo} alt={player.team.shortName} class="leaders__team-logo">
										<div class="leaders__team-info">
											<span class="leaders__team-name">{player.team.name}</span>
											<!--<span class="leaders__position">{player.position}</span>-->
										</div>
									</div>
								</div>
							</li>
							{/each}							
						</ul>
						{/if}
					</div>

					<div class="leaders__col" data-panel="rebounds">
						<h3 class="leaders__col-title">Подборы</h3>
						{#if loadingRebounds}
							<div class="widget-status"><ilb-loader size="64px"></ilb-loader></div>
						{:else if rebounds.length > 0}
						<ul class="leaders__list">
							{#each rebounds as player, index}
							<li class="leaders__player" on:click={() => selectPlayer(player)}>
								<div class="leaders__avatar">
									<img src={player.player.photo} alt={player.player.lastName} class="leaders__photo {index === 0 ? '--orange' : ''}">
									<span class="leaders__rank {index === 0 ? '--orange' : ''}">{player.rank}</span>
								</div>
								<div class="leaders__info">
									<div class="leaders__info-top">
										<div class="leaders__name">
											<span>{player.player.lastName}</span>
											<div class="leaders__name-row">
												<span>{player.player.firstName}</span>
												<!--<span class="leaders__jersey">#{player.jersey}</span>-->
											</div>
										</div>
										<span class="leaders__score">{player.displayValue}</span>
									</div>
									<div class="leaders__team">
										<img src={player.team.logo} alt={player.team.shortName} class="leaders__team-logo">
										<div class="leaders__team-info">
											<span class="leaders__team-name">{player.team.name}</span>
											<!--<span class="leaders__position">{player.position}</span>-->
										</div>
									</div>
								</div>
							</li>
							{/each}
						</ul>
						{/if}
					</div>

					<div class="leaders__col" data-panel="assists">
						<h3 class="leaders__col-title">Передачи</h3>
						{#if loadingAssists}
							<div class="widget-status"><ilb-loader size="64px"></ilb-loader></div>
						{:else if assists.length > 0}
						<ul class="leaders__list">
							{#each assists as player, index}
							<li class="leaders__player" on:click={() => selectPlayer(player)}>
								<div class="leaders__avatar">
									<img src={player.player.photo} alt={player.player.lastName} class="leaders__photo {index === 0 ? '--orange' : ''}">
									<span class="leaders__rank {index === 0 ? '--orange' : ''}">{player.rank}</span>
								</div>
								<div class="leaders__info">
									<div class="leaders__info-top">
										<div class="leaders__name">
											<span>{player.player.lastName}</span>
											<div class="leaders__name-row">
												<span>{player.player.firstName}</span>
												<!--<span class="leaders__jersey">#{player.jersey}</span>-->
											</div>
										</div>
										<span class="leaders__score">{player.displayValue}</span>
									</div>
									<div class="leaders__team">
										<img src={player.team.logo} alt={player.team.shortName} class="leaders__team-logo">
										<div class="leaders__team-info">
											<span class="leaders__team-name">{player.team.name}</span>
											<!--<span class="leaders__position">{player.position}</span>-->
										</div>
									</div>
								</div>
							</li>
							{/each}
						</ul>
						{/if}
					</div>
				</div>
			</div>
		</section>
	{/if}
</div>

<style>
	:host { display: block; }
	.leaders__player { cursor: pointer; }
</style>
